<template>
  <component
    :is="inline ? 'div' : 'v-dialog'"
    v-bind="outerBind"
    @update:model-value="onDialogUpdate"
  >
    <template v-if="inline">
      <LoadingOverlay :show="saving" text="저장 중..." variant="save" />
      <LoadingOverlay :show="deleting" text="삭제 중..." variant="delete" />
      <CommonWageDetailBoard
        :form="form"
        :display-calc="displayCalc"
        :deduct-form="deductForm"
        :manual-flags="manualFlags"
        v-model:production-nontax="settings.cwhd_production_nontax"
        v-model:lunch-expense="settings.cwhd_lunch_expense"
        v-model:is-owner="settings.cwhd_is_owner"
        v-model:basic-calc-mode="basicCalcMode"
        :basic-calc-mode-allowed="basicCalcModeAllowed"
        :show-production-worker-option="showProductionWorkerOption"
        :production-nontax-allowed="productionWorkerValidation.canApply"
        :min-hourly-wage="minHourlyWage"
        :year="year"
        :disabled="saving || deleting"
        :pension-exempt="ageInsurance.pensionExempt"
        :employment-exempt="ageInsurance.employmentExempt"
        :lunch-invalid="!!(lunchValidation && !lunchValidation.isValid)"
        :lunch-warning="lunchValidation && !lunchValidation.isValid ? lunchValidation.message : ''"
        @amount-input="onAmountInput"
        @hours-input="onHoursInput"
        @hours-blur="onHoursBlur"
        @hourly-blur="onHourlyBlur"
        @toggle-manual="onToggleManual"
        @open-manual="openDeductionFixModal"
        @deduct-input="onDeductInput"
        @deduct-blur="onDeductBlur"
        @calc-amount-input="onCalcAmountInput"
        @calc-amount-blur="onCalcAmountBlur"
      />
    </template>

    <div v-else class="emp-detail-modal">
      <div class="emp-detail-header">
        <div class="emp-info">
          <select
            v-if="employeeOptions.length > 1"
            v-model="selectedEmpKey"
            class="emp-select"
            @change="onEmployeeSelectChange"
          >
            <option v-for="emp in employeeOptions" :key="rowEmpKey(emp)" :value="rowEmpKey(emp)">
              {{ emp.ce_name }} ({{ emp.ce_position || '-' }})
            </option>
          </select>
          <span v-else class="emp-select emp-name-only">{{ employeeName }}</span>
          <span v-if="currentRow?.ce_join_date" class="emp-join">입사 {{ currentRow.ce_join_date }}</span>
        </div>
        <button type="button" class="close-btn" @click="emit('close')">&times;</button>
      </div>

      <div class="emp-detail-body">
        <div class="emp-box settings-box">
          <div class="box-title">설정</div>
          <div class="settings-grid">
            <div v-if="showProductionWorkerOption" class="setting-check">
              <input
                id="cw-production-nontax"
                v-model="settings.cwhd_production_nontax"
                type="checkbox"
                :disabled="!productionWorkerValidation.canApply && !settings.cwhd_production_nontax"
              />
              <label for="cw-production-nontax" class="check-text">생산직근로자 비과세 적용</label>
              <v-tooltip v-if="!productionWorkerValidation.canApply" location="top">
                <template #activator="{ props: tipProps }">
                  <v-icon v-bind="tipProps" size="16" color="warning" class="setting-warning-icon">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ productionWorkerValidation.usualMessage || productionWorkerValidation.positionMessage }}</span>
              </v-tooltip>
            </div>
            <div class="setting-check">
              <input id="cw-lunch-expense" v-model="settings.cwhd_lunch_expense" type="checkbox" />
              <label for="cw-lunch-expense" class="check-text">중식비 실비변상 처리</label>
              <div v-if="lunchValidation && !lunchValidation.isValid" class="lunch-expense-warning">
                <v-icon size="14" color="warning">mdi-alert</v-icon>
                <span>{{ lunchValidation.message }}</span>
              </div>
            </div>
            <div class="setting-check">
              <input id="cw-is-owner" v-model="settings.cwhd_is_owner" type="checkbox" />
              <label for="cw-is-owner" class="check-text">대표자</label>
              <v-tooltip location="top">
                <template #activator="{ props: tipProps }">
                  <v-icon v-bind="tipProps" size="14" color="info" class="setting-info-icon">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>대표자는 고용/실업/산재보험, 퇴직금이 면제됩니다</span>
              </v-tooltip>
            </div>
          </div>
        </div>

        <div class="emp-input-row">
          <div class="emp-box input-box work-info">
            <div class="box-title box-title--with-action">
              <span>
                근로정보
                <span v-if="minHourlyWage" class="min-wage-inline">
                  ({{ year }}년 최저 {{ formatWageNumber(minHourlyWage) }}원)
                </span>
              </span>
              <div v-if="basicCalcModeAllowed" class="wage-basic-calc-mode" role="group" aria-label="기본급 산출 방식">
                <button
                  type="button"
                  class="wage-basic-calc-mode__btn"
                  :class="{ 'wage-basic-calc-mode__btn--active': basicCalcMode === 'hourly' }"
                  :disabled="saving || deleting"
                  @click="basicCalcMode = 'hourly'"
                >시급×시간</button>
                <button
                  type="button"
                  class="wage-basic-calc-mode__btn"
                  :class="{ 'wage-basic-calc-mode__btn--active': basicCalcMode === 'basic' }"
                  :disabled="saving || deleting"
                  @click="basicCalcMode = 'basic'"
                >기본급→시간</button>
              </div>
            </div>
            <table class="input-table">
              <tbody>
              <tr>
                <td class="wage-input-cell hourly-wage-item">
                  <label>시급</label>
                  <div class="wage-input-wrapper">
                    <CommonWageHourlyInput
                      v-model="form.cwhd_pay_calc_hourly"
                      :min-hourly-wage="minHourlyWage"
                      :disabled="saving || deleting"
                    />
                  </div>
                </td>
                <td>
                  <label>일수</label>
                  <input :value="form.cwhd_work_days" type="text" @input="handleHoursInput('cwhd_work_days', $event)" @blur="onHoursBlur('cwhd_work_days')" />
                </td>
                <td>
                  <label>시간</label>
                  <input
                    :value="form.cwhd_work_hours"
                    type="text"
                    :disabled="saving || deleting || basicCalcMode === 'basic'"
                    @input="handleHoursInput('cwhd_work_hours', $event)"
                    @blur="onHoursBlur('cwhd_work_hours')"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <label>연장</label>
                  <input :value="form.cwhd_overtime_hours" type="text" @input="handleHoursInput('cwhd_overtime_hours', $event)" @blur="onHoursBlur('cwhd_overtime_hours')" />
                </td>
                <td>
                  <label>야간</label>
                  <input :value="form.cwhd_night_hours" type="text" @input="handleHoursInput('cwhd_night_hours', $event)" @blur="onHoursBlur('cwhd_night_hours')" />
                </td>
                <td>
                  <label>휴일</label>
                  <input :value="form.cwhd_holiday_hours" type="text" @input="handleHoursInput('cwhd_holiday_hours', $event)" @blur="onHoursBlur('cwhd_holiday_hours')" />
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="emp-box input-box allowance-info">
            <div class="box-title">수당 항목</div>
            <table class="input-table">
              <tbody>
              <tr>
                <td>
                  <label>연차</label>
                  <input v-model="form.cwhd_dayoff" type="text" @input="onAmountInput('cwhd_dayoff')" />
                </td>
                <td>
                  <label>장기</label>
                  <input v-model="form.cwhd_senior" type="text" @input="onAmountInput('cwhd_senior')" />
                </td>
                <td>
                  <label>선임</label>
                  <input v-model="form.cwhd_sunim" type="text" @input="onAmountInput('cwhd_sunim')" />
                </td>
                <td>
                  <label>상여</label>
                  <input v-model="form.cwhd_bonus" type="text" @input="onAmountInput('cwhd_bonus')" />
                </td>
              </tr>
              <tr>
                <td>
                  <label>교육</label>
                  <input v-model="form.cwhd_bosu" type="text" @input="onAmountInput('cwhd_bosu')" />
                </td>
                <td>
                  <label>보존</label>
                  <input v-model="form.cwhd_bojon" type="text" @input="onAmountInput('cwhd_bojon')" />
                </td>
                <td>
                  <label>기타</label>
                  <input v-model="form.cwhd_other" type="text" @input="onAmountInput('cwhd_other')" />
                </td>
                <td></td>
              </tr>
              <tr class="nontax-row">
                <td>
                  <label>당직</label>
                  <input v-model="form.cwhd_dang" type="text" @input="onAmountInput('cwhd_dang')" />
                </td>
                <td :class="{ 'lunch-error': lunchValidation && !lunchValidation.isValid }">
                  <label>중식비</label>
                  <input v-model="form.cwhd_lunch" type="text" @input="onAmountInput('cwhd_lunch')" />
                </td>
                <td>
                  <label>차량</label>
                  <input v-model="form.cwhd_car" type="text" @input="onAmountInput('cwhd_car')" />
                </td>
                <td>
                  <label>양육</label>
                  <input v-model="form.cwhd_child" type="text" @input="onAmountInput('cwhd_child')" />
                </td>
              </tr>
              </tbody>
            </table>
            <div v-if="lunchValidation && !lunchValidation.isValid" class="lunch-warning">
              <span class="warning-icon">⚠</span>
              <span class="warning-text">{{ lunchValidation.message }}</span>
            </div>
          </div>
        </div>

        <div class="emp-box result-box-new">
          <div class="result-header">
            <span class="result-title">임금구성</span>
            <span class="adjust-tags">
              <span class="tag total-tag">임금합계 <b>{{ formatWageNumber(displayCalc.cwhd_wage_total) }}</b></span>
              <span class="tag">과세 <b>{{ formatWageNumber(displayCalc.cwhd_taxable) }}</b></span>
              <span class="tag">통상 <b>{{ formatWageNumber(displayCalc.cwhd_usual) }}</b></span>
            </span>
          </div>
          <table class="result-table">
            <tbody>
            <tr>
              <td>
                <span>기본급</span>
                <input
                  v-if="basicCalcMode === 'basic'"
                  type="text"
                  class="result-basic-input"
                  :value="formatWageNumber(displayCalc.cwhd_basic)"
                  :disabled="saving || deleting"
                  @input="onCalcAmountInput('cwhd_basic', ($event.target as HTMLInputElement).value)"
                  @blur="onCalcAmountBlur('cwhd_basic', $event)"
                />
                <span v-else>{{ formatWageNumber(displayCalc.cwhd_basic) }}</span>
              </td>
              <td><span>연장(과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_overtime) }}</span></td>
              <td><span>야간(과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_night) }}</span></td>
              <td><span>휴일(과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_holiday) }}</span></td>
            </tr>
            <tr
              v-if="settings.cwhd_production_nontax && (displayCalc.cwhd_overtime_nontax > 0 || displayCalc.cwhd_night_nontax > 0 || displayCalc.cwhd_holiday_nontax > 0)"
              class="nontax-row"
            >
              <td></td>
              <td><span>연장(비과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_overtime_nontax) }}</span></td>
              <td><span>야간(비과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_night_nontax) }}</span></td>
              <td><span>휴일(비과세)</span><span>{{ formatWageNumber(displayCalc.cwhd_holiday_nontax) }}</span></td>
            </tr>
            <tr>
              <td><span>연차수당</span><span>{{ form.cwhd_dayoff || '0' }}</span></td>
              <td><span>장기근속</span><span>{{ form.cwhd_senior || '0' }}</span></td>
              <td><span>선임수당</span><span>{{ form.cwhd_sunim || '0' }}</span></td>
              <td><span>상여금</span><span>{{ form.cwhd_bonus || '0' }}</span></td>
            </tr>
            <tr>
              <td><span>교육비</span><span>{{ form.cwhd_bosu || '0' }}</span></td>
              <td><span>보존수당</span><span>{{ form.cwhd_bojon || '0' }}</span></td>
              <td><span>기타수당</span><span>{{ form.cwhd_other || '0' }}</span></td>
              <td></td>
            </tr>
            <tr class="nontax-row">
              <td><span>당직수당</span><span>{{ form.cwhd_dang || '0' }}</span></td>
              <td><span>중식비</span><span>{{ form.cwhd_lunch || '0' }}</span></td>
              <td><span>차량보조비</span><span>{{ form.cwhd_car || '0' }}</span></td>
              <td><span>양육비</span><span>{{ form.cwhd_child || '0' }}</span></td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="emp-deduct-settings">
          <div class="emp-two-col">
            <div class="emp-box deduct-box">
              <div class="box-title">
                <span>근로자 공제</span>
              </div>
              <div class="deduct-edit-grid">
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.yeon" @change="onManualFlagChange('yeon', $event)" />
                    <span>국민연금</span>
                    <span v-if="ageInsurance.pensionExempt && ageInsurance.age !== null" class="age-exempt-badge">만 {{ ageInsurance.age }}세 면제</span>
                    <span v-else-if="ageInsurance.age !== null" class="age-auto-badge">만 {{ ageInsurance.age }}세 자동적용</span>
                  </label>
                  <input
                    v-if="manualFlags.yeon"
                    type="number"
                    class="deduct-input"
                    :value="deductForm.cwhd_l_yeon"
                    @input="onDeductInput('cwhd_l_yeon', $event)"
                    @blur="onDeductBlur('cwhd_l_yeon', $event)"
                  />
                  <span v-else class="deduct-auto-value">
                    {{ ageInsurance.pensionExempt ? '0 (60세 이상 면제)' : formatWageNumber(displayCalc.cwhd_l_yeon) }}
                  </span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.gun" @change="onManualFlagChange('gun', $event)" />
                    <span>건강보험</span>
                  </label>
                  <input
                    v-if="manualFlags.gun"
                    type="number"
                    class="deduct-input"
                    :value="deductForm.cwhd_l_gun"
                    @input="onDeductInput('cwhd_l_gun', $event)"
                    @blur="onDeductBlur('cwhd_l_gun', $event)"
                  />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_gun) }}</span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.jang" @change="onManualFlagChange('jang', $event)" />
                    <span>장기요양</span>
                  </label>
                  <input
                    v-if="manualFlags.jang"
                    type="number"
                    class="deduct-input"
                    :value="deductForm.cwhd_l_jang"
                    @input="onDeductInput('cwhd_l_jang', $event)"
                    @blur="onDeductBlur('cwhd_l_jang', $event)"
                  />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_jang) }}</span>
                </div>
                <!-- 고용보험 [근로] -->
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.l_go" @change="onManualFlagChange('l_go', $event)" />
                    <span>고용보험</span>
                    <span class="deduct-side-chip deduct-side-chip--labor">근로</span>
                    <span v-if="ageInsurance.employmentExempt && ageInsurance.age !== null" class="age-exempt-badge">만 {{ ageInsurance.age }}세 면제</span>
                  </label>
                  <input v-if="manualFlags.l_go" v-model.number="deductForm.cwhd_l_go" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">
                    {{ ageInsurance.employmentExempt ? '0 (65세 이상 면제)' : formatWageNumber(displayCalc.cwhd_l_go) }}
                  </span>
                </div>
                <!-- 고용보험 [기관] -->
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.c_go" @change="onManualFlagChange('c_go', $event)" />
                    <span>고용보험</span>
                    <span class="deduct-side-chip deduct-side-chip--corp">기관</span>
                    <span v-if="settings.cwhd_is_owner" class="age-exempt-badge">대표자 면제</span>
                  </label>
                  <input v-if="manualFlags.c_go" v-model.number="deductForm.cwhd_c_go" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">
                    {{ settings.cwhd_is_owner ? '0 (대표자 면제)' : formatWageNumber(displayCalc.cwhd_c_go) }}
                  </span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.l_so" @change="onManualFlagChange('l_so', $event)" />
                    <span>소득세</span>
                  </label>
                  <input v-if="manualFlags.l_so" v-model.number="deductForm.cwhd_l_so" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_so) }}</span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.l_ji" @change="onManualFlagChange('l_ji', $event)" />
                    <span>지방세</span>
                  </label>
                  <input v-if="manualFlags.l_ji" v-model.number="deductForm.cwhd_l_ji" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_ji) }}</span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.l_jiwon" @change="onManualFlagChange('l_jiwon', $event)" />
                    <span>지원공제</span>
                  </label>
                  <input v-if="manualFlags.l_jiwon" v-model.number="deductForm.cwhd_l_jiwon" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_jiwon) }}</span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.l_sik" @change="onManualFlagChange('l_sik', $event)" />
                    <span>중식비공제</span>
                  </label>
                  <input v-if="manualFlags.l_sik" v-model.number="deductForm.cwhd_l_sik" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">{{ formatWageNumber(displayCalc.cwhd_l_sik) }}</span>
                </div>
                <!-- 수동입력 전용 (자동계산 없음 → 체크박스 없이 항상 인풋) -->
                <div class="deduct-edit-row">
                  <label class="deduct-label deduct-label--manual-only">
                    <span>기타공제</span>
                  </label>
                  <input v-model.number="deductForm.cwhd_l_etc" type="number" class="deduct-input" placeholder="0" />
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label deduct-label--manual-only">
                    <span>연말정산</span>
                  </label>
                  <input v-model.number="deductForm.cwhd_l_yearend" type="number" class="deduct-input" placeholder="0" />
                </div>
              </div>
              <div class="deduct-total">
                <span>합계</span><span>{{ formatWageNumber(displayCalc.cwhd_l_total) }}원</span>
              </div>
            </div>
            <div class="emp-box deduct-box">
              <div class="box-title">
                <span>기관 공제</span>
              </div>
              <div class="deduct-edit-grid">
                <!-- 4대보험(국민연금, 건강, 장기요양, 고용)은 근로자 공제 섹션에서 관리 -->
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.c_sil" @change="onManualFlagChange('c_sil', $event)" />
                    <span>실업급여</span>
                  </label>
                  <input v-if="manualFlags.c_sil" v-model.number="deductForm.cwhd_c_sil" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">
                    {{ settings.cwhd_is_owner ? '0 (대표자 면제)' : formatWageNumber(displayCalc.cwhd_c_sil) }}
                  </span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.c_san" @change="onManualFlagChange('c_san', $event)" />
                    <span>산재보험</span>
                  </label>
                  <input v-if="manualFlags.c_san" v-model.number="deductForm.cwhd_c_san" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">
                    {{ settings.cwhd_is_owner ? '0 (대표자 면제)' : formatWageNumber(displayCalc.cwhd_c_san) }}
                  </span>
                </div>
                <div class="deduct-edit-row">
                  <label class="deduct-label">
                    <input type="checkbox" :checked="!!manualFlags.c_out" @change="onManualFlagChange('c_out', $event)" />
                    <span>퇴직금</span>
                  </label>
                  <input v-if="manualFlags.c_out" v-model.number="deductForm.cwhd_c_out" type="number" class="deduct-input" />
                  <span v-else class="deduct-auto-value">
                    {{ settings.cwhd_is_owner ? '0 (대표자 면제)' : formatWageNumber(displayCalc.cwhd_c_out) }}
                  </span>
                </div>
              </div>
              <div class="deduct-total">
                <span>합계</span><span>{{ formatWageNumber(displayCalc.cwhd_c_total) }}원</span>
              </div>
            </div>
          </div>
        </div>

        <div class="emp-box" style="margin-top: 8px">
          <div class="box-title">메모</div>
          <textarea v-model="form.cwhd_memo" rows="2" placeholder="메모 입력" class="memo-textarea" />
        </div>

        <div class="emp-summary">
          <div class="summary-item total">
            <span>임금합계</span><span>{{ formatWageNumber(displayCalc.cwhd_wage_total) }}원</span>
          </div>
          <div class="summary-item minus">
            <span>공제합계</span><span>{{ formatWageNumber(displayCalc.cwhd_l_total) }}원</span>
          </div>
          <div class="summary-item net">
            <span>실수령액</span><span>{{ formatWageNumber(displayCalc.netPay) }}원</span>
          </div>
        </div>
      </div>

      <div class="emp-detail-footer">
        <div class="footer-left">
          <button
            v-if="currentRow?.cwhd_idx"
            type="button"
            class="btn-delete-inline"
            :disabled="saving || deleting"
            @click="onDelete"
          >
            삭제
          </button>
        </div>
        <div class="footer-right">
          <button type="button" class="btn-save" :disabled="saving" @click="onSave">
            {{ saving ? '저장 중...' : '저장' }}
          </button>
          <button v-if="!inline" type="button" class="btn-close" @click="emit('close')">닫기</button>
        </div>
      </div>
    </div>
  </component>

  <HomeWageDeductionFixModal
    v-model="showDeductionFixModal"
    :title="deductionFixModalTitle"
    :has-auto-preview="deductionFixModalPreview.hasAuto"
    :auto-amount="deductionFixModalPreview.nominal"
    :auto-label="deductionFixAutoLabel"
    :auto-button-label="deductionFixAutoButtonLabel"
    :auto-hint="deductionFixAutoHint"
    :applied-amount="deductionFixAppliedAmount"
    :applied-notes="deductionFixModalPreview.notes"
    :formula="deductionFixModalPreview.formula"
    :current-amount="deductionFixModalCurrentAmount"
    :has-current-amount="deductionFixModalHasCurrent"
    manual-label="직접 입력"
    :manual-display="deductionFixModalManualDisplay"
    input-id="common-wage-deduction-fix-manual"
    :format-money="formatWageNumber"
    @cancel="cancelDeductionFixModal"
    @apply-auto="applyDeductionFixModalNominal"
    @apply-applied="applyDeductionFixModalApplied"
    @apply-manual="applyDeductionFixModalManual"
    @manual-input="onDeductionFixModalManualInput"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { commonWageApi } from '@/services/api';
import CommonWageHourlyInput from '@/components/wage/common/CommonWageHourlyInput.vue';
import CommonWageDetailBoard from '@/components/wage/common/CommonWageDetailBoard.vue';
import HomeWageDeductionFixModal from '@/components/wage/home/HomeWageDeductionFixModal.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import {
  COMMON_WAGE_INSURANCE_SHARED,
} from '@/policies/commonWagePayrollFields';
import type { CommonWageListRow, CommonWageTaxRate } from '@/types/commonWage';
import {
  formatWageNumber,
  parseWageNumber,
  parseHours,
  sanitizeHoursInput,
  normalizeHoursInputValue,
  formatHoursDisplay,
  validateAgeInsurance,
  validateProductionWorkerNontax,
  validateLunchExpense,
  clampNontaxCashFieldInput,
  isProductionWorkerPosition,
} from '@/composables/wage/useWageCalculator';
import { truncateWageOnes } from '@/policies/wageOnesTruncatePolicy';
import {
  computeCommonWageDetail,
  computeCommonWageDetailForDisplay,
  computeCommonWageDetailForSave,
  previewCommonWageDeductionFix,
  taxRateInfoToRates,
  defaultProductionNontax,
  applyWageAmountOverrides,
  WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY,
  WAGE_HOURS_DERIVED_CALC_KEYS,
  stripHoursDerivedManualFlags,
  type IncomeTaxRow,
  type CommonWageCalcResult,
  type CommonWageManualFlags,
  type CommonWageDeductForm,
  type CommonWageSettings,
  type CommonWageCalcInput,
} from '@/composables/wage/useCommonWageCalc';
import type { CcwdFormInputField } from '@/constants/commonWageFields';
import { rowEmpKey, resolveAuthoritativeBirth } from '@/utils/empMatchKey';
import {
  createEmptyFormStrings,
  rowToFormStrings,
  getRowToFormDefaultOptions,
  rowToDeductNumbers,
  rowToSettings,
  calcToDeductNumbers,
  buildSavePayload,
  emptyDeductNumbers,
  formStringsToNumbers,
  inferWorkDaysFromHours,
  rowToCalcSnapshot,
  parseManualFlagsFromRow,
  type CommonWageFormStrings,
} from '@/utils/commonWageMapper';

const props = withDefaults(
  defineProps<{
    show: boolean;
    row: CommonWageListRow | null;
    employees?: CommonWageListRow[];
    mCode: string;
    year: number;
    month: number;
    bizGubun: string;
    minHourlyWage?: number;
    taxRateInfo?: CommonWageTaxRate | null;
    /** true면 v-dialog 없이 메인 영역에 표시 */
    inline?: boolean;
    /** 자동계산 모드 — 항상 ON (입력 변경 시 즉시 연동 계산) */
    autoCalcMode?: boolean;
    /** 기본급 산출 방식 토글 사용 가능 여부 (관리자 권한 80 이상) */
    basicCalcModeAllowed?: boolean;
    /**
     * 근무일수 미입력 시 넣을 일수(주말·공휴일 제외).
     * null이면 공휴일 조회 전이라 비워 둔다.
     */
    defaultWorkDays?: number | null;
  }>(),
  { inline: false, autoCalcMode: true, basicCalcModeAllowed: false, defaultWorkDays: null }
);

const isPanelActive = computed(() => (props.inline ? !!props.row : props.show));

const outerBind = computed(() => {
  if (props.inline) {
    return { class: 'emp-detail-inline-root' };
  }
  return {
    modelValue: props.show,
    maxWidth: 920,
    persistent: true,
    scrollable: true,
  };
});

const emit = defineEmits<{
  close: [];
  saved: [];
  deleted: [];
  change: []; // ★ 입력 변경 시 부모에게 알림
  hydrated: []; // ★ 폼 로드·자동계산 완료 (dirty 해제용)
}>();

/** DB/연월 로드·자동계산 중 — blur 등으로 change가 올라가지 않도록 */
const suppressChangeNotify = ref(false);

const notifyChange = () => {
  if (suppressChangeNotify.value || saving.value || deleting.value) return;
  emit('change');
};

const saving = ref(false);
const deleting = ref(false);
const calculating = ref(false);
/** DB 저장값 로드 후 — 자동 재계산이 저장값을 덮어쓰지 않도록 */
const payrollDbLock = ref(false);
/** 「계산」 버튼으로 스냅샷 확정 여부 (저장 전 필수) */
const payrollCalcConfirmed = ref(false);
const skipCalcConfirmReset = ref(false);
const autoCalcSyncing = ref(false);
/** 자동계산 모드 — 2-pass 계산 결과 (「계산」 버튼과 동일) */
const autoCalcSnapshot = ref<CommonWageCalcResult | null>(null);
/** 근무·수당 금액 수동 입력값 */
const calcAmountOverrides = ref<Partial<Record<string, number>>>({});
const incomeTaxTable = ref<IncomeTaxRow[]>([]);
const incomeTaxYearNote = ref('');

const settings = ref<Pick<CommonWageSettings, 'cwhd_production_nontax' | 'cwhd_lunch_expense' | 'cwhd_is_owner'>>({
  cwhd_production_nontax: false,
  cwhd_lunch_expense: false,
  cwhd_is_owner: false,
});

const manualFlags = ref<CommonWageManualFlags>({});
const prevMonthLunch = ref<number | null>(null);
const lunchValidation = ref<ReturnType<typeof validateLunchExpense> | null>(null);

/** 기본급 산출 방식 — 시급×시간(기본) 또는 기본급→시간 역산 */
const basicCalcMode = ref<'hourly' | 'basic'>('hourly');

const deductForm = ref<CommonWageDeductForm>(emptyDeductNumbers());

const selectedEmpKey = ref<string | null>(null);
const currentRow = ref<CommonWageListRow | null>(null);

const employeeOptions = computed(() => props.employees?.length ? props.employees : props.row ? [props.row] : []);
const employeeName = computed(() => currentRow.value?.ce_name || '');
const employeeBirthDate = computed(
  () => currentRow.value?.ce_birth_date || currentRow.value?.cwhd_birth || null
);
const year = computed(() => props.year);
const month = computed(() => props.month);

function onEmployeeSelectChange() {
  const emp = employeeOptions.value.find((e) => rowEmpKey(e) === selectedEmpKey.value);
  if (emp) {
    currentRow.value = emp;
    void hydrateFormFromRow(emp);
  }
}

const showProductionWorkerOption = computed(() => {
  const pos = currentRow.value?.ce_position;
  return isProductionWorkerPosition(pos) || settings.value.cwhd_production_nontax;
});

const form = ref<CommonWageFormStrings>(createEmptyFormStrings());
/** 달력 영업일로 채운 값. 사용자가 고치면 null */
const autoFilledWorkDays = ref<number | null>(null);
const workDaysTouched = ref(false);

const emptyCalc = (): CommonWageCalcResult => ({
  cwhd_basic: 0,
  overtimeTotal: 0,
  nightTotal: 0,
  holidayTotal: 0,
  cwhd_overtime: 0,
  cwhd_night: 0,
  cwhd_holiday: 0,
  cwhd_overtime_nontax: 0,
  cwhd_night_nontax: 0,
  cwhd_holiday_nontax: 0,
  cwhd_wage_total: 0,
  cwhd_taxable: 0,
  cwhd_usual: 0,
  cwhd_l_yeon: 0,
  cwhd_l_gun: 0,
  cwhd_l_jang: 0,
  cwhd_l_go: 0,
  cwhd_l_so: 0,
  cwhd_l_ji: 0,
  cwhd_l_jiwon: 0,
  cwhd_l_sik: 0,
  cwhd_l_etc: 0,
  cwhd_l_yearend: 0,
  cwhd_l_total: 0,
  cwhd_c_yeon: 0,
  cwhd_c_gun: 0,
  cwhd_c_jang: 0,
  cwhd_c_go: 0,
  cwhd_c_sil: 0,
  cwhd_c_san: 0,
  cwhd_c_out: 0,
  cwhd_c_total: 0,
  netPay: 0,
});

const dbCalcSnapshot = ref<CommonWageCalcResult>(emptyCalc());

const fallbackCalc = computed((): CommonWageCalcResult => {
  const parsed = formStringsToNumbers(form.value);
  const hourly = parsed.cwhd_pay_calc_hourly;
  const overtimeTotal = truncateWageOnes(
    hourly * parsed.cwhd_overtime_hours * 1.5,
    props.year,
    props.month
  );
  const nightTotal = truncateWageOnes(hourly * parsed.cwhd_night_hours * 0.5, props.year, props.month);
  const holidayTotal = truncateWageOnes(
    hourly * parsed.cwhd_holiday_hours * 1.5,
    props.year,
    props.month
  );
  const cwhd_basic = basicCalcMode.value === 'basic' && calcAmountOverrides.value.cwhd_basic != null
    ? calcAmountOverrides.value.cwhd_basic
    : truncateWageOnes(hourly * parsed.cwhd_work_hours, props.year, props.month);
  const cwhd_wage_total =
    cwhd_basic +
    overtimeTotal +
    nightTotal +
    holidayTotal +
    parsed.cwhd_dayoff +
    parsed.cwhd_senior +
    parsed.cwhd_sunim +
    parsed.cwhd_bonus +
    parsed.cwhd_bosu +
    parsed.cwhd_bojon +
    parsed.cwhd_other +
    parsed.cwhd_dang +
    parsed.cwhd_lunch +
    parsed.cwhd_car +
    parsed.cwhd_child;
  return {
    ...emptyCalc(),
    cwhd_basic,
    overtimeTotal,
    nightTotal,
    holidayTotal,
    cwhd_overtime: overtimeTotal,
    cwhd_night: nightTotal,
    cwhd_holiday: holidayTotal,
    cwhd_wage_total,
    cwhd_taxable: cwhd_wage_total,
    cwhd_usual: cwhd_basic,
    netPay: cwhd_wage_total,
  };
});

const buildCalcInput = (): CommonWageCalcInput => ({
  form: {
    ...formStringsToNumbers(form.value),
    // 기본급→시간 모드에서는 수동 입력 기본급을 계산에 반영
    ...(basicCalcMode.value === 'basic' && calcAmountOverrides.value.cwhd_basic != null
      ? { cwhd_basic: calcAmountOverrides.value.cwhd_basic }
      : {}),
  },
  settings: {
    ...settings.value,
    manualFlags: manualFlags.value,
    deductForm: deductForm.value,
  },
  taxRates: taxRateInfoToRates(props.taxRateInfo!),
  incomeTaxTable: incomeTaxTable.value,
  birthDate: employeeBirthDate.value,
  year: props.year,
  month: props.month,
  rowContext: {
    ce_join_date: currentRow.value?.ce_join_date,
    ce_leave_date: currentRow.value?.ce_leave_date,
    is_month_enter: currentRow.value?.is_month_enter,
    is_month_leave: currentRow.value?.is_month_leave,
    cwhd_idx: currentRow.value?.cwhd_idx,
    cwhd_taxable: currentRow.value?.cwhd_taxable,
  },
});

const calcResult = computed((): CommonWageCalcResult => {
  if (!props.taxRateInfo) return fallbackCalc.value;
  return computeCommonWageDetailForDisplay(buildCalcInput());
});

function computeFreshCalc(): CommonWageCalcResult {
  if (!props.taxRateInfo) return fallbackCalc.value;
  return computeCommonWageDetailForDisplay(buildCalcInput());
}

function computeFreshCalcForSave(): CommonWageCalcResult {
  if (!props.taxRateInfo) return fallbackCalc.value;
  return computeCommonWageDetailForSave(buildCalcInput());
}

function mergeDisplayCalc(base: CommonWageCalcResult): CommonWageCalcResult {
  return applyWageAmountOverrides(
    base,
    calcAmountOverrides.value,
    manualFlags.value,
    formStringsToNumbers(form.value),
    settings.value.cwhd_production_nontax
  );
}

const displayCalc = computed(() => {
  if (props.autoCalcMode) {
    return mergeDisplayCalc(calcResult.value);
  }
  const base =
    payrollDbLock.value && payrollCalcConfirmed.value ? dbCalcSnapshot.value : calcResult.value;
  let merged = mergeDisplayCalc(base);
  // DB 잠금 스냅샷의 과세가 회계(중식 채움) 기준이어도, 화면 과세·합계는 입력 중식 기준으로 맞춤
  // (비과세액 합계 = 입력 중식·차량·양육과 일치). 공제·실수령은 회계 기준 유지.
  if (settings.value.cwhd_lunch_expense && props.taxRateInfo) {
    const facility = computeCommonWageDetail(buildCalcInput());
    merged = {
      ...merged,
      cwhd_basic: facility.cwhd_basic,
      cwhd_overtime: facility.cwhd_overtime,
      cwhd_night: facility.cwhd_night,
      cwhd_holiday: facility.cwhd_holiday,
      cwhd_overtime_nontax: facility.cwhd_overtime_nontax,
      cwhd_night_nontax: facility.cwhd_night_nontax,
      cwhd_holiday_nontax: facility.cwhd_holiday_nontax,
      overtimeTotal: facility.overtimeTotal,
      nightTotal: facility.nightTotal,
      holidayTotal: facility.holidayTotal,
      cwhd_wage_total: facility.cwhd_wage_total,
      cwhd_taxable: facility.cwhd_taxable,
      cwhd_usual: facility.cwhd_usual,
      cwhd_lunch: formStringsToNumbers(form.value).cwhd_lunch,
      netPay: Math.max(0, facility.cwhd_wage_total - (Number(merged.cwhd_l_total) || 0)),
    };
  }
  return merged;
});

const unlockPayrollLedgerForEdit = () => {
  payrollDbLock.value = false;
};

function clearWageAmountOverride(calcKey: string) {
  if (!(calcKey in calcAmountOverrides.value)) return;
  const next = { ...calcAmountOverrides.value };
  delete next[calcKey];
  calcAmountOverrides.value = next;
}

function resolveCalcForSave(): CommonWageCalcResult {
  const base = props.autoCalcMode
    ? (autoCalcSnapshot.value ?? computeFreshCalcForSave())
    : (() => {
        // 잠금 스냅샷이 표시용(채움)일 수 있어 저장은 항상 지급칸 원본+회계공제로 재산출
        return computeFreshCalcForSave();
      })();
  const merged = mergeDisplayCalc(base);
  
  // 수동 입력된 공제값을 calc에 병합 (manualFlags가 true인 필드만)
  const f = manualFlags.value;
  const df = deductForm.value;
  if (f.yeon) { merged.cwhd_l_yeon = df.cwhd_l_yeon; merged.cwhd_c_yeon = df.cwhd_c_yeon; }
  if (f.gun) { merged.cwhd_l_gun = df.cwhd_l_gun; merged.cwhd_c_gun = df.cwhd_c_gun; }
  if (f.jang) { merged.cwhd_l_jang = df.cwhd_l_jang; merged.cwhd_c_jang = df.cwhd_c_jang; }
  if (f.l_go) merged.cwhd_l_go = df.cwhd_l_go;
  if (f.c_go) merged.cwhd_c_go = df.cwhd_c_go;
  if (f.l_so) merged.cwhd_l_so = df.cwhd_l_so;
  if (f.l_ji) merged.cwhd_l_ji = df.cwhd_l_ji;
  if (f.l_jiwon) merged.cwhd_l_jiwon = df.cwhd_l_jiwon;
  if (f.l_sik) merged.cwhd_l_sik = df.cwhd_l_sik;
  // 수동입력 전용 (자동계산 없음 → 항상 deductForm 값 사용)
  merged.cwhd_l_etc = df.cwhd_l_etc || 0;
  merged.cwhd_l_yearend = df.cwhd_l_yearend || 0;
  if (f.c_sil) merged.cwhd_c_sil = df.cwhd_c_sil;
  if (f.c_san) merged.cwhd_c_san = df.cwhd_c_san;
  if (f.c_out) merged.cwhd_c_out = df.cwhd_c_out;
  
  // 합계 재계산
  merged.cwhd_l_total = merged.cwhd_l_yeon + merged.cwhd_l_gun + merged.cwhd_l_jang + merged.cwhd_l_go
    + merged.cwhd_l_so + merged.cwhd_l_ji + merged.cwhd_l_jiwon + merged.cwhd_l_sik + merged.cwhd_l_etc + merged.cwhd_l_yearend;
  merged.cwhd_c_total = merged.cwhd_c_yeon + merged.cwhd_c_gun + merged.cwhd_c_jang + merged.cwhd_c_go
    + merged.cwhd_c_sil + merged.cwhd_c_san + merged.cwhd_c_out;
  
  return merged;
}

function getManualFlagForDeductKey(key: string): keyof CommonWageManualFlags | null {
  const pair = COMMON_WAGE_INSURANCE_SHARED.find((p) => p.laborKey === key || p.corpKey === key);
  if (pair) return pair.key as keyof CommonWageManualFlags;
  const map: Record<string, keyof CommonWageManualFlags> = {
    cwhd_l_go: 'l_go',
    cwhd_c_go: 'c_go',
    cwhd_l_so: 'l_so',
    cwhd_l_ji: 'l_ji',
    cwhd_l_jiwon: 'l_jiwon',
    cwhd_l_sik: 'l_sik',
    cwhd_l_etc: 'l_etc',
    cwhd_l_yearend: 'l_yearend',
    cwhd_c_sil: 'c_sil',
    cwhd_c_san: 'c_san',
    cwhd_c_out: 'c_out',
  };
  return map[key] ?? null;
}

function ensureDeductManual(key: string) {
  const flag = getManualFlagForDeductKey(key);
  if (!flag || manualFlags.value[flag]) return;
  const autoVal = Number((displayCalc.value as unknown as Record<string, number>)[key]) || 0;
  (deductForm.value as Record<string, number>)[key] = autoVal;
  const pair = COMMON_WAGE_INSURANCE_SHARED.find((p) => p.laborKey === key || p.corpKey === key);
  if (pair) {
    (deductForm.value as Record<string, number>)[pair.laborKey] = autoVal;
    (deductForm.value as Record<string, number>)[pair.corpKey] = autoVal;
  }
  manualFlags.value = { ...manualFlags.value, [flag]: true };
}

function onCalcAmountInput(key: string, rawValue?: string) {
  if (key === 'cwhd_basic' && basicCalcMode.value === 'basic') {
    const raw = String(rawValue ?? '').replace(/[^\d]/g, '');
    const num = raw ? parseInt(raw, 10) : 0;
    calcAmountOverrides.value = { ...calcAmountOverrides.value, [key]: num };
    manualFlags.value = { ...manualFlags.value, w_basic: true };
    syncWorkHoursFromBasic();
    syncDeductFormFromCalc();
    notifyChange();
    return;
  }
  if (WAGE_HOURS_DERIVED_CALC_KEYS.has(key)) return;
  const raw = String(rawValue ?? '').replace(/[^\d]/g, '');
  const num = raw ? parseInt(raw, 10) : 0;
  const flag = WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY[key];
  if (!flag) return;
  calcAmountOverrides.value = { ...calcAmountOverrides.value, [key]: num };
  manualFlags.value = { ...manualFlags.value, [flag]: true };
  syncDeductFormFromCalc();
  notifyChange();
}

function onCalcAmountBlur(key: string, event: Event) {
  if (key === 'cwhd_basic' && basicCalcMode.value === 'basic') {
    const num = parseWageNumber((event.target as HTMLInputElement).value);
    calcAmountOverrides.value = { ...calcAmountOverrides.value, [key]: num };
    manualFlags.value = { ...manualFlags.value, w_basic: true };
    (event.target as HTMLInputElement).value = num ? formatWageNumber(num) : '';
    syncWorkHoursFromBasic();
    syncDeductFormFromCalc();
    return;
  }
  if (WAGE_HOURS_DERIVED_CALC_KEYS.has(key)) return;
  const num = parseWageNumber((event.target as HTMLInputElement).value);
  const flag = WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY[key];
  if (!flag) return;
  calcAmountOverrides.value = { ...calcAmountOverrides.value, [key]: num };
  manualFlags.value = { ...manualFlags.value, [flag]: true };
  (event.target as HTMLInputElement).value = num ? formatWageNumber(num) : '';
  syncDeductFormFromCalc();
}

/** 기본급→시간 모드: 기본급·시급으로 근무시간 역산 */
function syncWorkHoursFromBasic() {
  if (basicCalcMode.value !== 'basic') return;
  const basic = Number(calcAmountOverrides.value.cwhd_basic) || 0;
  const hourly = parseWageNumber(form.value.cwhd_pay_calc_hourly);
  if (basic > 0 && hourly > 0) {
    const hours = Math.round((basic / hourly) * 10) / 10;
    form.value.cwhd_work_hours = formatHoursDisplay(hours);
  }
}

/** 자동계산 전 — 시급 미입력 시 DB 스냅샷/기본급으로 역산 (OFF→ON 시 기본급 0 방지) */
function ensureFormForAutoCalc() {
  const parsed = formStringsToNumbers(form.value);
  if (parsed.cwhd_pay_calc_hourly > 0) return;

  const hours = parsed.cwhd_work_hours;
  const snapBasic = Number(dbCalcSnapshot.value.cwhd_basic) || 0;
  if (snapBasic > 0 && hours > 0) {
    form.value.cwhd_pay_calc_hourly = formatWageNumber(Math.round(snapBasic / hours));
    return;
  }

  const rowBasic = Number(currentRow.value?.cwhd_basic) || 0;
  if (rowBasic > 0 && hours > 0) {
    form.value.cwhd_pay_calc_hourly = formatWageNumber(Math.round(rowBasic / hours));
    return;
  }

  // 근무시간이 입력된 경우에만 최저시급 제안 (미입력 상태에서 UI 기본값 주입 방지)
  if (hours > 0 && props.minHourlyWage) {
    form.value.cwhd_pay_calc_hourly = formatWageNumber(props.minHourlyWage);
  }
}

/** 자동계산 모드 — 입력·설정 변경 시 공제 연동 재계산 */
function applyAutoCalc() {
  if (!props.autoCalcMode || !props.taxRateInfo || autoCalcSyncing.value) return;
  autoCalcSyncing.value = true;
  skipCalcConfirmReset.value = true;
  try {
    unlockPayrollLedgerForEdit();
    ensureFormForAutoCalc();
    ensureWorkDaysInForm();
    if (basicCalcMode.value === 'basic') {
      syncWorkHoursFromBasic();
    }
    const pass1 = computeFreshCalc();
    syncDeductFormFromAuto(pass1);
    const freshDisplay = computeFreshCalc();
    const freshSave = computeFreshCalcForSave();
    autoCalcSnapshot.value = { ...freshSave };
    payrollCalcConfirmed.value = Number(freshDisplay.cwhd_wage_total) > 0;
  } finally {
    autoCalcSyncing.value = false;
    void nextTick(() => {
      skipCalcConfirmReset.value = false;
    });
  }
}

function formWorkDaysNumber(): number {
  return parseInt(String(form.value.cwhd_work_days).replace(/[^\d]/g, ''), 10) || 0;
}

function markAutoWorkDaysFromForm(row: CommonWageListRow | null) {
  if (row && Number(row.cwhd_work_days) > 0) {
    autoFilledWorkDays.value = null;
    return;
  }
  const current = formWorkDaysNumber();
  autoFilledWorkDays.value = current > 0 ? current : null;
}

/** 저장된 근무일수가 없고 사용자가 아직 고치지 않았으면 달력 영업일을 넣는다 */
function applyCalendarWorkDaysDefault() {
  if (workDaysTouched.value) return;
  const fallback = Number(props.defaultWorkDays);
  if (!Number.isFinite(fallback) || fallback <= 0) return;
  const row = currentRow.value;
  if (row && Number(row.cwhd_work_days) > 0) return;
  const next = Math.floor(fallback);
  const current = formWorkDaysNumber();
  if (current > 0 && current !== autoFilledWorkDays.value) return;
  if (current === next) {
    autoFilledWorkDays.value = next;
    return;
  }
  const wasSuppress = suppressChangeNotify.value;
  suppressChangeNotify.value = true;
  skipCalcConfirmReset.value = true;
  form.value.cwhd_work_days = String(next);
  autoFilledWorkDays.value = next;
  void nextTick(() => {
    suppressChangeNotify.value = wasSuppress;
    skipCalcConfirmReset.value = false;
  });
}

/** 근무일수 미입력(0)이면 달력 영업일, 없으면 근무시간÷8 — 계산·저장 시 임금대장 일수 반영 */
function ensureWorkDaysInForm() {
  const days = formWorkDaysNumber();
  if (days > 0) return;
  const fallback = Number(props.defaultWorkDays);
  if (Number.isFinite(fallback) && fallback > 0) {
    form.value.cwhd_work_days = String(Math.floor(fallback));
    autoFilledWorkDays.value = Math.floor(fallback);
    return;
  }
  if (props.defaultWorkDays === null) return;
  const hours = parseHours(form.value.cwhd_work_hours);
  if (hours <= 0) return;
  const inferred = inferWorkDaysFromHours(hours);
  if (inferred != null) {
    form.value.cwhd_work_days = String(inferred);
    autoFilledWorkDays.value = inferred;
  }
}

const runWageCalc = () => {
  if (props.autoCalcMode) {
    applyAutoCalc();
    return;
  }
  if (saving.value || deleting.value) return;
  if (!props.taxRateInfo) {
    alert('세율 정보를 불러오지 못했습니다.');
    return;
  }
  calculating.value = true;
  skipCalcConfirmReset.value = true;
  try {
    unlockPayrollLedgerForEdit();
    ensureWorkDaysInForm();
    if (basicCalcMode.value === 'basic') {
      syncWorkHoursFromBasic();
    }
    // 1차: 임금·과세 기준 자동 공제 산출 → 2차: 비수동 공제 필드 반영 후 최종 계산
    syncDeductFormFromAuto(computeFreshCalc());
    const fresh = computeFreshCalcForSave();
    dbCalcSnapshot.value = { ...fresh };
    payrollDbLock.value = true;
    payrollCalcConfirmed.value = Number(fresh.cwhd_wage_total) > 0;
    if (!payrollCalcConfirmed.value) {
      alert('시급·근무시간 등 근무정보를 입력해 주세요.');
    }
  } finally {
    calculating.value = false;
    void nextTick(() => {
      skipCalcConfirmReset.value = false;
    });
  }
};

const runDeductCalc = () => {
  runWageCalc();
};

const applyPrevMonthRow = (prev: CommonWageListRow) => {
  skipCalcConfirmReset.value = true;
  calcAmountOverrides.value = {};
  workDaysTouched.value = false;
  form.value = rowToFormStrings(prev, props.minHourlyWage, {
    skipWorkHoursDefault: true,
    defaultWorkDays: props.defaultWorkDays ?? null,
  });
  markAutoWorkDaysFromForm(prev);
  settings.value = rowToSettings(prev);
  applyDefaultProductionNontax(currentRow.value?.ce_position);
  manualFlags.value = stripHoursDerivedManualFlags(parseManualFlagsFromRow(prev.cwhd_manual_flags));
  deductForm.value = rowToDeductNumbers(prev);
  // 전월 합계는 당월 세율·간이세액표와 다를 수 있음 — 재계산 전에는 DB 스냅샷 잠금 해제
  dbCalcSnapshot.value = emptyCalc();
  payrollDbLock.value = false;
  payrollCalcConfirmed.value = false;
  void nextTick(() => {
    skipCalcConfirmReset.value = false;
  });
  notifyChange();
};

const importPrevMonthData = async () => {
  const row = currentRow.value;
  if (!row || !props.mCode) return false;
  try {
    const res = await commonWageApi.getPrevMonthWage({
      mCode: props.mCode,
      year: props.year,
      month: props.month,
      bizGubun: props.bizGubun,
      name: row.cwhd_name || row.ce_name || '',
      birth: resolveAuthoritativeBirth(row) || '',
    });
    if (!res.data?.success || !res.data.data) {
      alert('전월 급여 데이터가 없습니다.');
      return false;
    }
    applyPrevMonthRow(res.data.data as CommonWageListRow);
    validateLunchInput();
    await nextTick();
    applyAutoCalc();
    alert('지난달 근무·설정을 반영하고 당월 기준으로 자동 재계산했습니다.\n「저장」 버튼으로 저장하세요.');
    return true;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`전월 데이터 가져오기 실패: ${message}`);
    return false;
  }
};

defineExpose({ importPrevMonthData, runWageCalc, runDeductCalc, saveWage: onSave, deleteWage: onDelete });

const ageInsurance = computed(() =>
  validateAgeInsurance(employeeBirthDate.value, props.year, props.month)
);

const productionWorkerValidation = computed(() => {
  const usualBeforeNontax = calcResult.value.cwhd_usual;
  return validateProductionWorkerNontax(currentRow.value?.ce_position, usualBeforeNontax);
});

/** 방문요양과 동일 — 대상 직종이면 생산직 비과세 기본 ON (통상 한도 초과는 watch가 해제) */
function applyDefaultProductionNontax(position?: string | null) {
  if (isProductionWorkerPosition(position)) {
    settings.value.cwhd_production_nontax = true;
  }
}

watch(
  () => settings.value.cwhd_lunch_expense,
  () => {
    validateLunchInput();
    if (props.autoCalcMode) applyAutoCalc();
    else syncDeductFormFromCalc();
  }
);

watch(
  () => productionWorkerValidation.value.canApply,
  (canApply) => {
    if (!canApply && settings.value.cwhd_production_nontax) {
      settings.value.cwhd_production_nontax = false;
    }
  }
);

function syncDeductFormFromAuto(calc: CommonWageCalcResult) {
  const auto = calcToDeductNumbers(calc);
  const f = manualFlags.value;
  // 3대보험 (근로·기관 동일)
  if (!f.yeon) {
    deductForm.value.cwhd_l_yeon = auto.cwhd_l_yeon;
    deductForm.value.cwhd_c_yeon = auto.cwhd_c_yeon;
  }
  if (!f.gun) {
    deductForm.value.cwhd_l_gun = auto.cwhd_l_gun;
    deductForm.value.cwhd_c_gun = auto.cwhd_c_gun;
  }
  if (!f.jang) {
    deductForm.value.cwhd_l_jang = auto.cwhd_l_jang;
    deductForm.value.cwhd_c_jang = auto.cwhd_c_jang;
  }
  // 고용보험 (근로·기관 분리)
  if (!f.l_go) deductForm.value.cwhd_l_go = auto.cwhd_l_go;
  if (!f.c_go) deductForm.value.cwhd_c_go = auto.cwhd_c_go;
  // 근로자 기타 공제
  if (!f.l_so) deductForm.value.cwhd_l_so = auto.cwhd_l_so;
  if (!f.l_ji) deductForm.value.cwhd_l_ji = auto.cwhd_l_ji;
  if (!f.l_jiwon) deductForm.value.cwhd_l_jiwon = auto.cwhd_l_jiwon;
  if (!f.l_sik) deductForm.value.cwhd_l_sik = auto.cwhd_l_sik;
  // l_etc, l_yearend — 수동입력 전용, 자동 덮어쓰기 금지
  // 기관 기타 공제
  if (!f.c_sil) deductForm.value.cwhd_c_sil = auto.cwhd_c_sil;
  if (!f.c_san) deductForm.value.cwhd_c_san = auto.cwhd_c_san;
  if (!f.c_out) deductForm.value.cwhd_c_out = auto.cwhd_c_out;
}

function syncDeductFormFromCalc() {
  if (props.autoCalcMode) {
    applyAutoCalc();
    return;
  }
  syncDeductFormFromAuto(displayCalc.value);
}

function clearWageOverrideForManualFlag(field: keyof CommonWageManualFlags) {
  const calcKey = Object.entries(WAGE_AMOUNT_MANUAL_FLAG_BY_CALC_KEY).find(([, f]) => f === field)?.[0];
  if (calcKey) clearWageAmountOverride(calcKey);
}

const MANUAL_FLAG_TO_DEDUCT_KEYS: Record<string, string[]> = {
  yeon: ['cwhd_l_yeon', 'cwhd_c_yeon'],
  gun: ['cwhd_l_gun', 'cwhd_c_gun'],
  jang: ['cwhd_l_jang', 'cwhd_c_jang'],
  l_go: ['cwhd_l_go'],
  c_go: ['cwhd_c_go'],
  l_so: ['cwhd_l_so'],
  l_ji: ['cwhd_l_ji'],
  l_jiwon: ['cwhd_l_jiwon'],
  l_sik: ['cwhd_l_sik'],
  l_etc: ['cwhd_l_etc'],
  l_yearend: ['cwhd_l_yearend'],
  c_sil: ['cwhd_c_sil'],
  c_san: ['cwhd_c_san'],
  c_out: ['cwhd_c_out'],
};

function copyAutoValueToDeductForm(field: keyof CommonWageManualFlags) {
  const keys = MANUAL_FLAG_TO_DEDUCT_KEYS[field];
  if (!keys) return;
  for (const key of keys) {
    const autoVal = Number((displayCalc.value as unknown as Record<string, number>)[key]) || 0;
    (deductForm.value as Record<string, number>)[key] = autoVal;
  }
}

const MANUAL_FLAG_LABELS: Record<string, string> = {
  yeon: '국민연금',
  gun: '건강보험',
  jang: '장기요양',
  l_go: '고용보험(근로)',
  c_go: '고용보험(기관)',
  l_so: '소득세',
  l_ji: '지방세',
  l_jiwon: '지원공제',
  l_sik: '중식비공제',
  l_etc: '기타공제',
  l_yearend: '연말정산',
  c_sil: '실업급여',
  c_san: '산재보험',
  c_out: '퇴직금',
  w_basic: '기본급',
  w_overtime: '연장수당',
  w_night: '야간수당',
  w_holiday: '휴일수당',
  w_ot_nontax: '연장(비과세)',
  w_n_ntax: '야간(비과세)',
  w_h_ntax: '휴일(비과세)',
};

function onManualFlagChange(field: keyof CommonWageManualFlags, event: Event) {
  if (saving.value || deleting.value) return;
  const checked = (event.target as HTMLInputElement).checked;
  if (MANUAL_FLAG_TO_DEDUCT_KEYS[field]) {
    onToggleManual(field, checked);
    return;
  }
  if (!checked) {
    clearWageOverrideForManualFlag(field);
  }
  manualFlags.value = { ...manualFlags.value, [field]: checked };
  syncDeductFormFromCalc();
  notifyChange();
}

function onToggleManual(field: keyof CommonWageManualFlags | string, checked: boolean) {
  const key = field as keyof CommonWageManualFlags;
  if (!checked) {
    clearWageOverrideForManualFlag(key);
    manualFlags.value = { ...manualFlags.value, [key]: false };
    syncDeductFormFromCalc();
    notifyChange();
    return;
  }
  copyAutoValueToDeductForm(key);
  manualFlags.value = { ...manualFlags.value, [key]: true };
  syncDeductFormFromCalc();
  notifyChange();
  openDeductionFixModal(key, true);
}

const showDeductionFixModal = ref(false);
const deductionFixModalTarget = ref<keyof CommonWageManualFlags | null>(null);
const deductionFixModalManualDisplay = ref('');
/** 체크해서 연 창을 취소하면 체크를 되돌린다 */
const deductionFixOpenedFromCheck = ref(false);

const deductionFixModalPreview = computed(() => {
  if (!deductionFixModalTarget.value || !props.taxRateInfo) {
    return { hasAuto: false, applied: 0, nominal: 0, formula: '', notes: undefined as string | undefined };
  }
  return previewCommonWageDeductionFix(buildCalcInput(), deductionFixModalTarget.value);
});

const deductionFixAmountsDiffer = computed(
  () =>
    deductionFixModalPreview.value.hasAuto &&
    deductionFixModalPreview.value.applied !== deductionFixModalPreview.value.nominal
);

const deductionFixAutoLabel = computed(() =>
  deductionFixAmountsDiffer.value ? '요율 기준 산출액' : '자동계산 금액'
);

const deductionFixAutoButtonLabel = computed(() =>
  deductionFixAmountsDiffer.value ? '요율 기준 금액 사용' : '자동계산 금액 사용'
);

const deductionFixAutoHint = computed(() =>
  deductionFixAmountsDiffer.value
    ? '면제 조건을 적용하지 않고, 정해진 요율만으로 계산한 금액입니다.'
    : ''
);

const deductionFixAppliedAmount = computed(() =>
  deductionFixAmountsDiffer.value ? deductionFixModalPreview.value.applied : null
);

const deductionFixModalTitle = computed(() => {
  const key = deductionFixModalTarget.value;
  if (!key) return '금액 설정';
  return `${MANUAL_FLAG_LABELS[key] || '공제'} 금액 설정`;
});

const deductionFixModalCurrentAmount = computed(() => {
  const key = deductionFixModalTarget.value;
  if (!key) return 0;
  const deductKey = MANUAL_FLAG_TO_DEDUCT_KEYS[key]?.[0];
  if (!deductKey) return 0;
  return Number((deductForm.value as Record<string, number>)[deductKey]) || 0;
});

const deductionFixModalHasCurrent = computed(
  () =>
    !!deductionFixModalTarget.value &&
    !!manualFlags.value[deductionFixModalTarget.value] &&
    deductionFixModalCurrentAmount.value !== deductionFixModalPreview.value.nominal &&
    deductionFixModalCurrentAmount.value !== deductionFixModalPreview.value.applied
);

function openDeductionFixModal(field: keyof CommonWageManualFlags, fromCheck = false) {
  if (!MANUAL_FLAG_TO_DEDUCT_KEYS[field]) return;
  deductionFixOpenedFromCheck.value = fromCheck;
  deductionFixModalTarget.value = field;
  const deductKey = MANUAL_FLAG_TO_DEDUCT_KEYS[field][0];
  const current = Number((deductForm.value as Record<string, number>)[deductKey]) || 0;
  deductionFixModalManualDisplay.value = current ? formatWageNumber(current) : '';
  showDeductionFixModal.value = true;
}

function closeDeductionFixModal() {
  showDeductionFixModal.value = false;
  deductionFixModalTarget.value = null;
  deductionFixModalManualDisplay.value = '';
  deductionFixOpenedFromCheck.value = false;
}

function cancelDeductionFixModal() {
  const field = deductionFixModalTarget.value;
  const revert = deductionFixOpenedFromCheck.value;
  closeDeductionFixModal();
  if (!revert || !field) return;
  manualFlags.value = { ...manualFlags.value, [field]: false };
  syncDeductFormFromCalc();
  notifyChange();
}

function applyDeductionFixAmount(amount: number) {
  const field = deductionFixModalTarget.value;
  if (!field) return;
  const keys = MANUAL_FLAG_TO_DEDUCT_KEYS[field] || [];
  for (const key of keys) {
    (deductForm.value as Record<string, number>)[key] = Math.max(0, Math.floor(amount));
  }
  manualFlags.value = { ...manualFlags.value, [field]: true };
  syncDeductFormFromCalc();
  notifyChange();
  closeDeductionFixModal();
}

function applyDeductionFixModalNominal() {
  applyDeductionFixAmount(deductionFixModalPreview.value.nominal);
}

function applyDeductionFixModalApplied() {
  applyDeductionFixAmount(deductionFixModalPreview.value.applied);
}

function applyDeductionFixModalManual() {
  applyDeductionFixAmount(parseWageNumber(deductionFixModalManualDisplay.value));
}

function onDeductionFixModalManualInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value.replace(/[^\d]/g, '');
  deductionFixModalManualDisplay.value = raw ? formatWageNumber(Number(raw)) : '';
}

function onDeductInput(key: string, event: Event) {
  ensureDeductManual(key);
  const raw = (event.target as HTMLInputElement).value.replace(/[^\d]/g, '');
  const num = raw ? parseInt(raw, 10) : 0;
  (deductForm.value as Record<string, number>)[key] = num;
  const pair = COMMON_WAGE_INSURANCE_SHARED.find((p) => p.laborKey === key);
  if (pair) {
    (deductForm.value as Record<string, number>)[pair.corpKey] = num;
  }
  syncDeductFormFromCalc();
  notifyChange();
}

function onDeductBlur(key: string, event: Event) {
  // ensureDeductManual은 onDeductInput에서 이미 호출됨 — blur에서는 제거
  // (인풋에 포커스만 했다가 빠져나가도 체크박스가 체크되는 문제 방지)
  const num = parseWageNumber((event.target as HTMLInputElement).value);
  // 수동입력 플래그가 설정된 경우에만 값 저장
  const flag = getManualFlagForDeductKey(key);
  if (flag && manualFlags.value[flag]) {
    (deductForm.value as Record<string, number>)[key] = num;
    const pair = COMMON_WAGE_INSURANCE_SHARED.find((p) => p.laborKey === key);
    if (pair) {
      (deductForm.value as Record<string, number>)[pair.corpKey] = num;
    }
  }
  (event.target as HTMLInputElement).value = num ? formatWageNumber(num) : '';
  syncDeductFormFromCalc();
}

function validateLunchInput() {
  const lunch = parseWageNumber(form.value.cwhd_lunch);
  lunchValidation.value = validateLunchExpense(
    lunch,
    prevMonthLunch.value,
    settings.value.cwhd_lunch_expense
  );
}

async function loadPrevLunch(row: CommonWageListRow) {
  if (!props.mCode) return;
  try {
    const res = await commonWageApi.getPrevLunch({
      mCode: props.mCode,
      name: row.cwhd_name || row.ce_name || '',
      birth: resolveAuthoritativeBirth(row),
      year: props.year,
      month: props.month,
    });
    prevMonthLunch.value =
      res.data?.success && res.data.data?.prevLunch != null
        ? Number(res.data.data.prevLunch)
        : null;
  } catch {
    prevMonthLunch.value = null;
  }
  validateLunchInput();
}

async function loadIncomeTax() {
  incomeTaxTable.value = [];
  incomeTaxYearNote.value = '';
  try {
    const res = await commonWageApi.getIncomeTax(props.year);
    if (res.data?.success && Array.isArray(res.data.data)) {
      incomeTaxTable.value = res.data.data.map((row: any) => ({
        income1: Number(row.income1),
        income2: Number(row.income2),
        depe_1: Number(row.depe_1),
      }));
      if (res.data.isFallback && res.data.actualYear) {
        incomeTaxYearNote.value = `${res.data.actualYear}년 간이세액표 적용`;
      }
    }
  } catch (error: any) {
    console.error('간이세액표 로드 오류:', error);
  }
}

async function completeFormHydration() {
  if (props.autoCalcMode) {
    applyAutoCalc();
  }
  await nextTick();
  skipCalcConfirmReset.value = false;
  suppressChangeNotify.value = false;
  emit('hydrated');
}

function resolveBasicCalcMode(row: CommonWageListRow): 'hourly' | 'basic' {
  if (!props.basicCalcModeAllowed) return 'hourly';
  if (row.cwhd_basic_calc_mode === 'basic' || row.cwhd_basic_calc_mode === 'hourly') {
    return row.cwhd_basic_calc_mode;
  }
  return 'basic';
}

function loadFormFromRow(row: CommonWageListRow) {
  suppressChangeNotify.value = true;
  skipCalcConfirmReset.value = true;
  autoCalcSnapshot.value = null;
  calcAmountOverrides.value = {};
  workDaysTouched.value = false;
  form.value = rowToFormStrings(row, props.minHourlyWage, {
    ...getRowToFormDefaultOptions(row),
    defaultWorkDays: props.defaultWorkDays ?? null,
  });
  markAutoWorkDaysFromForm(row);
  manualFlags.value = stripHoursDerivedManualFlags(parseManualFlagsFromRow(row.cwhd_manual_flags));
  // 관리자이고 DB에 산출 방식이 없으면 기본급→시간. 저장된 값이 있으면 그 값.
  basicCalcMode.value = resolveBasicCalcMode(row);
  if (basicCalcMode.value === 'basic' && row.cwhd_idx) {
    // 기본급→시간 모드: 저장된 기본급을 수동값으로 고정해야 표시값이 DB와 일치
    const savedBasic = Number(row.cwhd_basic) || 0;
    if (savedBasic > 0) {
      calcAmountOverrides.value = { cwhd_basic: savedBasic };
      manualFlags.value = { ...manualFlags.value, w_basic: true };
    }
  }

  if (row.cwhd_idx) {
    settings.value = rowToSettings(row);
    applyDefaultProductionNontax(row.ce_position);
    deductForm.value = rowToDeductNumbers(row);
    dbCalcSnapshot.value = rowToCalcSnapshot(row);
    payrollDbLock.value = true;
    payrollCalcConfirmed.value = Number(row.cwhd_wage_total) > 0;
  } else {
    settings.value = {
      cwhd_production_nontax: defaultProductionNontax(row.ce_position),
      cwhd_lunch_expense: false,
      cwhd_is_owner: false,
    };
    deductForm.value = emptyDeductNumbers();
    manualFlags.value = {};
    dbCalcSnapshot.value = emptyCalc();
    payrollDbLock.value = false;
    payrollCalcConfirmed.value = false;
  }

  void loadPrevLunch(row);
}

async function hydrateFormFromRow(row: CommonWageListRow) {
  loadFormFromRow(row);
  await loadIncomeTax();
  await completeFormHydration();
}

watch(
  [form, settings, manualFlags],
  () => {
    if (skipCalcConfirmReset.value) return;
    if (props.autoCalcMode) {
      applyAutoCalc();
      return;
    }
    if (payrollCalcConfirmed.value) {
      payrollCalcConfirmed.value = false;
    }
  },
  { deep: true }
);

watch(
  () => props.autoCalcMode,
  (enabled) => {
    if (!isPanelActive.value) return;
    if (enabled) {
      applyAutoCalc();
      return;
    }
    autoCalcSnapshot.value = null;
    const fresh = computeFreshCalc();
    dbCalcSnapshot.value = { ...fresh };
    payrollDbLock.value = true;
    payrollCalcConfirmed.value = Number(fresh.cwhd_wage_total) > 0;
  }
);

watch(incomeTaxTable, () => {
  if (props.autoCalcMode && isPanelActive.value) {
    applyAutoCalc();
  }
});

watch(
  () => props.taxRateInfo,
  (info) => {
    if (props.autoCalcMode && info && isPanelActive.value) {
      applyAutoCalc();
    }
  }
);

watch(basicCalcMode, (mode) => {
  if (!isPanelActive.value || suppressChangeNotify.value) return;
  if (mode === 'basic') {
    // 기본급→시간 전환: 현재 표시 기본급을 수동값으로 고정하고 시간 역산
    const currentBasic = Number(displayCalc.value.cwhd_basic) || 0;
    if (currentBasic > 0) {
      calcAmountOverrides.value = { ...calcAmountOverrides.value, cwhd_basic: currentBasic };
      manualFlags.value = { ...manualFlags.value, w_basic: true };
      syncWorkHoursFromBasic();
    }
  } else {
    // 시급×시간 전환: 기본급 수동값 해제
    clearWageAmountOverride('cwhd_basic');
    const next = { ...manualFlags.value };
    delete next.w_basic;
    manualFlags.value = next;
  }
  if (props.autoCalcMode) {
    applyAutoCalc();
  }
});

watch(
  [form, settings, deductForm, manualFlags],
  () => {
    if (skipCalcConfirmReset.value) return;
    if (props.autoCalcMode) return;
    if (payrollCalcConfirmed.value) {
      payrollCalcConfirmed.value = false;
    }
  },
  { deep: true }
);

function onAmountInput(field: CcwdFormInputField, rawValue?: string) {
  const raw = String(rawValue ?? form.value[field] ?? '').replace(/[^\d]/g, '');
  let num = raw ? parseInt(raw, 10) : 0;
  if (field === 'cwhd_lunch' || field === 'cwhd_car' || field === 'cwhd_child') {
    num = clampNontaxCashFieldInput(field, num);
  }
  form.value[field] = num ? formatWageNumber(num) : '';
  if (field === 'cwhd_lunch') validateLunchInput();
  notifyChange();
}

function handleHoursInput(field: CcwdFormInputField, event: Event) {
  const target = event.target as HTMLInputElement;
  const cleaned = onHoursInput(field, target.value);
  if (cleaned !== target.value) {
    target.value = cleaned;
  }
}

function onHoursInput(field: CcwdFormInputField, rawValue?: string): string {
  if (field === 'cwhd_work_days') {
    let cleaned = String(rawValue ?? form.value[field] ?? '').replace(/[^\d]/g, '');
    const num = parseInt(cleaned, 10) || 0;
    if (num > 31) cleaned = '31';
    workDaysTouched.value = true;
    autoFilledWorkDays.value = null;
    form.value[field] = cleaned;
    notifyChange();
    return cleaned;
  }

  let cleaned = sanitizeHoursInput(String(rawValue ?? form.value[field] ?? ''));
  const num = parseHours(cleaned);
  if (num >= 1000) {
    cleaned = '999.9';
  }
  form.value[field] = cleaned;
  notifyChange();
  return cleaned;
}

function clearHoursDerivedAmountOverrides() {
  for (const key of WAGE_HOURS_DERIVED_CALC_KEYS) {
    if (key === 'cwhd_basic' && basicCalcMode.value === 'basic') continue;
    clearWageAmountOverride(key);
  }
  const stripped = stripHoursDerivedManualFlags(manualFlags.value);
  if (basicCalcMode.value === 'basic' && manualFlags.value.w_basic) {
    stripped.w_basic = true;
  }
  manualFlags.value = stripped;
}

const PREMIUM_HOURS_FORM_FIELDS: CcwdFormInputField[] = [
  'cwhd_overtime_hours',
  'cwhd_night_hours',
  'cwhd_holiday_hours',
];

function onHoursBlur(field: CcwdFormInputField) {
  const before = form.value[field];
  if (field === 'cwhd_work_days') {
    const num = parseInt(String(form.value.cwhd_work_days).replace(/[^\d]/g, ''), 10) || 0;
    form.value.cwhd_work_days = num > 0 ? String(num) : '';
    if (form.value[field] !== before) notifyChange();
    return;
  }
  form.value[field] = normalizeHoursInputValue(form.value[field]);
  if (
    field === 'cwhd_work_hours' ||
    PREMIUM_HOURS_FORM_FIELDS.includes(field)
  ) {
    clearHoursDerivedAmountOverrides();
  }
  if (form.value[field] !== before) notifyChange();
}

function onHourlyBlur() {
  const before = form.value.cwhd_pay_calc_hourly;
  clearHoursDerivedAmountOverrides();
  const hourly = parseWageNumber(form.value.cwhd_pay_calc_hourly);
  if (props.minHourlyWage && hourly > 0 && hourly < props.minHourlyWage) {
    alert(`시급은 최저시급(${formatWageNumber(props.minHourlyWage)}원) 이상이어야 합니다.`);
    form.value.cwhd_pay_calc_hourly = formatWageNumber(props.minHourlyWage);
  } else if (hourly > 0) {
    form.value.cwhd_pay_calc_hourly = formatWageNumber(hourly);
  } else {
    form.value.cwhd_pay_calc_hourly = '';
  }
  if (basicCalcMode.value === 'basic') {
    syncWorkHoursFromBasic();
  }
  if (form.value.cwhd_pay_calc_hourly !== before) notifyChange();
}

watch(
  () => [isPanelActive.value, props.row, props.year, props.month, props.bizGubun] as const,
  async ([visible, row, year, month, bizGubun], oldTuple) => {
    if (visible && row) {
      const ym = `${year}${String(month).padStart(2, '0')}`;

      const [oldVisible, oldRow, oldYear, oldMonth, oldBizGubun] = oldTuple ?? [false, null, 0, 0, ''];
      const newKey = rowEmpKey(row);
      const oldKey = oldRow ? rowEmpKey(oldRow) : null;

      const employeeChanged = newKey !== oldKey;
      const periodChanged = year !== oldYear || month !== oldMonth || bizGubun !== oldBizGubun;
      const panelJustOpened = visible && !oldVisible;
      /** API 응답으로 row만 갱신될 때(월 props는 이미 변경됨) form 재로드 */
      const rowYmChanged =
        !!oldRow &&
        String(oldRow.cwhd_ym || '') !== String(row.cwhd_ym || '') &&
        rowEmpKey(oldRow) === newKey;

      // 저장된 행이면 반드시 현재 선택 연월과 일치해야 함 (이전 월 row 잔존 방지)
      if (row.cwhd_idx && String(row.cwhd_ym || '') !== ym) {
        return;
      }

      currentRow.value = row;

      if (employeeChanged || periodChanged || panelJustOpened || rowYmChanged) {
        selectedEmpKey.value = newKey;
        await hydrateFormFromRow(row);
      }
    } else if (!visible) {
      currentRow.value = null;
      selectedEmpKey.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => props.defaultWorkDays,
  () => {
    applyCalendarWorkDaysDefault();
  }
);

function onDialogUpdate(v: boolean) {
  if (!v) emit('close');
}

async function onSave() {
  const row = currentRow.value;
  if (!row || !props.mCode) return;

  const hourly = parseWageNumber(form.value.cwhd_pay_calc_hourly);
  if (props.minHourlyWage && hourly > 0 && hourly < props.minHourlyWage) {
    alert(`시급은 최저시급(${formatWageNumber(props.minHourlyWage)}원) 이상이어야 합니다.`);
    return;
  }

  validateLunchInput();
  if (lunchValidation.value && !lunchValidation.value.isValid) {
    alert(lunchValidation.value.message);
    return;
  }

  if (
    settings.value.cwhd_production_nontax &&
    !productionWorkerValidation.value.canApply
  ) {
    alert(
      productionWorkerValidation.value.usualMessage ||
        productionWorkerValidation.value.positionMessage ||
        '생산직근로자 비과세를 적용할 수 없습니다.'
    );
    return;
  }

  applyAutoCalc();

  ensureWorkDaysInForm();

  const calc = resolveCalcForSave();
  if (!Number(calc.cwhd_wage_total)) {
    alert('급여 합계가 0원입니다. 근무정보를 입력해 주세요.');
    return;
  }

  saving.value = true;
  try {
    const payload = buildSavePayload({
      meta: {
        m_code: props.mCode,
        cwhd_name: row.cwhd_name || row.ce_name || '',
        cwhd_birth: resolveAuthoritativeBirth(row),
        cwhd_ym: `${props.year}${String(props.month).padStart(2, '0')}`,
        cwhd_biz_gubun: props.bizGubun,
      },
      calc,
      form: form.value,
      settings: settings.value,
      manualFlags: manualFlags.value,
      basicCalcMode: basicCalcMode.value,
    });

    const res = await commonWageApi.save(payload);
    if (!res.data?.success) {
      throw new Error(res.data?.message || '저장 실패');
    }
    alert(res.data.message || '저장되었습니다.');
    emit('saved');
    if (!props.inline) emit('close');
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`임금 저장 실패: ${message}`);
  } finally {
    saving.value = false;
  }
}

async function onDelete() {
  const row = currentRow.value;
  if (!row?.cwhd_idx) return;
  if (!confirm(`${employeeName.value} 님의 ${props.year}년 ${props.month}월 임금 데이터를 삭제할까요?`)) {
    return;
  }

  deleting.value = true;
  try {
    const res = await commonWageApi.delete(row.cwhd_idx);
    if (!res.data?.success) throw new Error(res.data?.message || '삭제 실패');
    alert(res.data.message || '목록에서 제거되었습니다.');
    emit('deleted');
    if (!props.inline) {
      emit('close');
    }
  } catch (error: any) {
    alert(`삭제 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    deleting.value = false;
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/wage';

.emp-detail-inline-root {
  width: 100%;
}

.emp-detail-modal {
  &--inline {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;

    .emp-detail-body {
      max-height: none;
      overflow: visible;
    }

    .emp-detail-footer {
      position: sticky;
      bottom: 0;
      z-index: 2;
      background: #fff;
      border-top: 1px solid #e2e8f0;
    }
  }

  .memo-textarea {
    width: 100%;
    min-height: 48px;
    padding: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 13px;
    resize: vertical;
  }

  .btn-delete-inline {
    padding: 6px 12px;
    font-size: 12px;
    color: #c62828;
    background: #ffebee;
    border: 1px solid #ef9a9a;
    border-radius: 4px;
    cursor: pointer;
  }

  .nontax-hint {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    color: #2e7d32;
  }

  .emp-name-only {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
  }

  .min-wage-inline {
    font-size: 10px;
    font-weight: 500;
    color: #64748b;
    margin-left: 4px;
    text-transform: none;
    letter-spacing: 0;

    &::before {
      display: none;
    }
  }
}
</style>
